import type { Receipt, ReclassifyReceiptsResponse } from '@/api/client'

/**
 * Words and small sums for receipt classification (CAP-683), kept apart from
 * the components in pages/supplies-classification.tsx. Everything here reads
 * admin-api's fields; nothing infers a state the backend did not report.
 */

/** A pending receipt waiting longer than this is called out: the model is probably offline. */
const LONG_WAIT_MS = 24 * 60 * 60 * 1000

/** admin-api's fixed failure codes, in words. */
const ERROR_LABELS: Record<string, string> = {
  dead_lettered: 'The model kept failing on it',
  model_not_configured: 'No model is set up to classify receipts',
  queue_unavailable: 'It could not be sent to the model',
  enqueue_failed: 'It could not be sent to the model',
  request_failed: 'The request broke before it reached the model',
}

export function classificationErrorLabel(code: string | null): string {
  return (code && ERROR_LABELS[code]) || 'Failed for a reason admin-api did not name'
}

/** "Oct 3, 10:34 PM": a backend timestamp, shown in the reader's time zone. */
export function formatStamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function waitedLong(requestedAt: string | null, now: number): boolean {
  if (!requestedAt) return false
  const t = new Date(requestedAt).getTime()
  return !Number.isNaN(t) && now - t > LONG_WAIT_MS
}

export function unclassifiedLines(r: Receipt): number {
  return r.items.filter(i => i.category === null).length
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`
}

/** What a re-run did, from its own counts; never a guess at what the model will do. */
export function reclassifySentence(res: ReclassifyReceiptsResponse): string {
  if (res.requested === 0) return 'No receipt has an unclassified line.'
  const parts: string[] = []
  if (res.queued > 0) parts.push(`${plural(res.queued, 'receipt')} sent to the model`)
  if (res.completed > 0) {
    parts.push(`${plural(res.completed, 'receipt')} finished from rules and earlier answers`)
  }
  if (res.failed > 0) parts.push(`${plural(res.failed, 'receipt')} failed again`)
  return `${parts.join(', ')}.`
}
